import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TicketListModal from '../components/TicketListModal';
import { viewAsUserId } from '../lib/viewAs';
import { fetchMe } from '../lib/me';
import { fmtDate } from '../lib/when';
import { rememberOrder } from '../lib/ticketOrder';
import './UserDashboard.css';
import './UserDashboard.extra.css';

// Module scope: declared inside the component it would remount on every render.
// A button rather than a div, so the tiles are keyboard reachable.
//
// `short` is the word shown on a phone, where four tiles share one row and
// "Awaiting Approval" would wrap into three lines. The full label stays in
// the title and for screen readers.
function MyStat({ value, label, short, tone, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`Show ${label.toLowerCase()}`}
      aria-label={`${value} ${label}`}
      className="stat-tile is-clickable ud-stat"
    >
      <div className="ud-stat-value" style={{ color: tone }}>{value}</div>
      <div className="ud-stat-label" aria-hidden="true">
        <span className="ud-long">{label}</span>
        <span className="ud-short">{short || label}</span>
      </div>
    </button>
  );
}

// The list endpoint calls it `status`, the detail endpoint returns both. Read
// either, so a filter cannot silently match nothing again.
const statusOf = (t) => t.status || t.status_name || '';

/**
 * Which bucket a ticket belongs in.
 *
 * Every bucket used to be a hand-written list of status names:
 *
 *   active    ['Open', 'In Progress', 'On Hold']
 *   completed ['Resolved', 'Closed']
 *
 * which left 'Approved' - the state a ticket sits in once its hours have been
 * signed off and before it is billed and closed - in no bucket at all. The
 * ticket vanished from every tile and every filter except "All", so somebody
 * with two tickets saw one. Any status added later would have done the same,
 * silently.
 *
 * So only the two ends are named, and everything else is live work. A status
 * nobody has thought about now shows up as something to get on with, which is
 * the safe direction: the failure is a ticket in a slightly odd column, not a
 * ticket nobody can see.
 */
const DONE = ['Resolved', 'Closed'];
const AWAITING = 'Awaiting Approval';

const isAwaiting = (t) =>
  statusOf(t) === AWAITING || (t.approval_status === 'pending' && statusOf(t) === AWAITING);
const isDone = (t) => DONE.includes(statusOf(t));
const isActive = (t) => !isAwaiting(t) && !isDone(t);

function UserDashboard() {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  
  const [userTickets, setUserTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState(null);
  // When the server's list stops short of everything (lib/ticket-list.js
  // caps "all" at 5000), say so rather than count what arrived as the whole.
  const [capped, setCapped] = useState(null);   // { shown, total } or null
  const [filter, setFilter] = useState('all');
  const [drill, setDrill] = useState(null);
  // Whose tickets these are, once known: the words search asks for the same.
  const [ownerId, setOwnerId] = useState(null);

  /*
   * SEARCH (?q=; Gordon, 3 November - and where the search palette's "See
   * all in Tickets" lands). The words go to the server with the same scope
   * as the list - the tickets this person raised - and the server matches
   * them as the search box does: every word in the title, number or
   * description, or in a comment this person may read (routes/tickets-core.js,
   * `words`). The tiles keep counting all their tickets; the cards below are
   * the ones found.
   *
   * The address is written from window.location.search, never from a copy
   * React Router may not have caught up with (the licences lesson), and only
   * the answer to the latest words is ever shown (a request counter).
   */
  const location = useLocation();
  const urlQ = (new URLSearchParams(location.search).get('q') || '').trim();
  const [q, setQ] = useState(urlQ);
  const [found, setFound] = useState(null);     // null: not searching
  const [searching, setSearching] = useState(false);
  const asked = useRef(0);

  // The address moved on its own (the palette, Back): the box follows it.
  useEffect(() => {
    setQ((typed) => (typed.trim() === urlQ ? typed : urlQ));
  }, [urlQ]);

  // Typing: into the address 300 ms after the last key, replacing the entry.
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const words = q.trim();
      if ((params.get('q') || '') === words) return;
      if (words) params.set('q', words); else params.delete('q');
      const search = params.toString();
      navigate({ pathname: window.location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [q, navigate]);

  // The words in the address: asked of the server, latest answer only.
  useEffect(() => {
    const n = ++asked.current;
    if (!urlQ || !ownerId) { setFound(null); setSearching(false); return; }
    setSearching(true);
    axios.get(`${API_URL}/tickets`, {
      params: { envelope: 1, size: 'all', requester: ownerId, words: urlQ, sort: 'created_at', dir: 'desc' }
    })
      .then((r) => {
        if (n !== asked.current) return;
        setFound(Array.isArray(r.data) ? r.data : (r.data?.tickets ?? []));
      })
      .catch(() => { if (n === asked.current) setFound([]); })
      .finally(() => { if (n === asked.current) setSearching(false); });
  }, [API_URL, urlQ, ownerId]);

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      setProblem(null);
      // Whose tickets these are. An admin using "View as" sees that person's
      // tickets; otherwise it is the session's own user - the answer lib/me
      // already holds, rather than a second call to /auth/me.
      const impersonated = viewAsUserId();
      let user;
      if (impersonated) {
        const usersRes = await axios.get(`${API_URL}/reference-data`);
        const rows = Array.isArray(usersRes.data?.users) ? usersRes.data.users : [];
        user = rows.find((u) => String(u.id) === String(impersonated)) || { id: impersonated };
      } else {
        user = await fetchMe();
      }

      // There used to be a call to /api/users here, building a map of ids to
      // names that nothing in this file ever read.
      //
      // It was not merely wasted. /api/users is auth.requireAdmin, and this is
      // the page EVERY member of staff lands on - so for anyone who is not an
      // administrator it answered 403, the throw carried execution out through
      // the catch below, and setUserTickets was never reached. "My tickets"
      // has therefore shown an empty list to every ordinary user since that
      // endpoint was locked down, and to an administrator using View As, which
      // is how it surfaced: tickets raised as Rita reached the approvals queue
      // and then could not be seen by Rita.
      //
      // The ticket rows already carry requester_name and approver_name, so
      // there was never anything to look up.

      /*
       * THE TICKETS THIS PERSON RAISED, filtered by the server. This asked
       * for ?limit=500 and kept the rows whose requester_id matched, so past
       * five hundred tickets in the firm a person's own silently vanished
       * from "My tickets". The list endpoint takes `requester=` and the
       * envelope shape (routes/tickets-core.js, as All tickets uses it);
       * `size=all` is capped on the server and says so (`capped`), which
       * is then said here rather than counted as the whole.
       *
       * A bare array back means an older server that ignored both: the
       * browser-side filter is kept for that case only.
       */
      if (!user?.id) { setUserTickets([]); return; }
      setOwnerId(user.id);
      const ticketsRes = await axios.get(`${API_URL}/tickets`,
        { params: { envelope: 1, size: 'all', requester: user.id, sort: 'created_at', dir: 'desc' } });
      if (Array.isArray(ticketsRes.data)) {
        setUserTickets(ticketsRes.data.filter((t) => t.requester_id === user.id));
        setCapped(null);
      } else {
        const rows = ticketsRes.data?.tickets ?? [];
        setUserTickets(rows);
        setCapped(ticketsRes.data?.capped ? { shown: rows.length, total: ticketsRes.data.total } : null);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      // A 401 is handled once for every page by lib/apiClient: the session
      // is re-checked and the router sends them to sign in. This used to
      // navigate('/') here, which mounted this page again, which asked
      // again, which got 401 again - a loop with no message.
      setProblem(error.response?.data?.error
        || (error.response
          ? `Could not load your tickets (server returned ${error.response.status}).`
          : 'Could not reach the server to load your tickets.'));
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const shownTickets = urlQ && found ? found : userTickets;
  const filteredTickets = shownTickets.filter(ticket => {
    if (filter === 'all') return true;
    if (filter === 'awaiting-approval') return isAwaiting(ticket);
    if (filter === 'active') return isActive(ticket);
    if (filter === 'completed') return isDone(ticket);
    return statusOf(ticket) === filter;
  });
  // The order on screen, for the ticket page's previous / next ([ and ]).
  const orderKey = filteredTickets.map((t) => t.id).join(',');
  useEffect(() => { if (orderKey) rememberOrder(orderKey.split(',')); }, [orderKey]);

  const getStatusBadgeStyle = (ticket) => {
    let bgColor = 'var(--color-bg-alt)';
    let textColor = 'var(--color-text-secondary)';
    
    if (ticket.approval_status === 'pending' && statusOf(ticket) === 'Awaiting Approval') {
      bgColor = 'var(--color-info-light)';
      textColor = 'var(--color-info)';
    } else {
      const colors = {
        'Awaiting Approval': { bg: 'var(--color-info-light)', text: 'var(--color-info)' },
        'Open': { bg: 'var(--color-critical-light)', text: 'var(--color-critical)' },
        'In Progress': { bg: 'var(--color-info-light)', text: 'var(--color-info)' },
        'On Hold': { bg: 'var(--color-warning-light)', text: 'var(--color-warning)' },
        // The hours have been signed off and it is on its way to an invoice.
        // Missing here, so it fell through to the default grey and read as
        // something inert.
        'Approved': { bg: 'var(--color-success-light)', text: 'var(--color-success)' },
        'Resolved': { bg: 'var(--color-success-light)', text: 'var(--color-success)' },
        'Closed': { bg: 'var(--color-idle-light)', text: 'var(--color-idle)' }
      };
      const colorSet = colors[statusOf(ticket)];
      if (colorSet) {
        bgColor = colorSet.bg;
        textColor = colorSet.text;
      }
    }
    
    return { backgroundColor: bgColor, color: textColor };
  };

  const getPriorityBadgeStyle = (priority) => {
    const colors = {
      'Low': { bg: 'var(--color-info-light)', text: 'var(--color-info)' },
      'Medium': { bg: 'var(--color-warning-light)', text: 'var(--color-warning)' },
      'High': { bg: 'var(--color-critical-light)', text: 'var(--color-critical)' },
      'Critical': { bg: 'var(--color-critical-light)', text: 'var(--color-critical)' }
    };
    return colors[priority] || { bg: 'var(--color-idle-light)', text: 'var(--color-idle)' };
  };

  // One definition of what each bucket means, used by both the counts and the
  // drill-down lists so a tile can never disagree with the list behind it.
  const byBucket = (bucket) => {
    if (bucket === 'awaiting') return userTickets.filter(isAwaiting);
    if (bucket === 'active') return userTickets.filter(isActive);
    if (bucket === 'completed') return userTickets.filter(isDone);
    return userTickets;
  };

  const stats = {
    total: userTickets.length,
    awaiting: byBucket('awaiting').length,
    active: byBucket('active').length,
    completed: byBucket('completed').length
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg)',
        padding: '2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading your tickets...</p>
      </div>
    );
  }

  return (
    <div className="ud-page">
      {/* Header */}
      <div className="ud-head">
        <h1 className="ud-title">My tickets</h1>
        <p className="ud-sub">Track the status of tickets you've created</p>
      </div>

      {problem && (
        <p className="ud-problem" role="alert" style={{ color: 'var(--color-danger)', margin: '0 0 1rem' }}>
          {problem}{' '}
          <button type="button" className="btn btn-secondary btn-small" onClick={fetchUserData}>Try again</button>
        </p>
      )}
      {capped && (
        <p className="ud-capped" role="status" style={{ color: 'var(--color-text-secondary)', margin: '0 0 1rem' }}>
          Showing the first {capped.shown} of {capped.total} tickets; the counts below are of those.
        </p>
      )}

      {/* Stats Cards - each opens the list behind its number */}
      <div className="ud-stats">
        <MyStat value={stats.total} label="Total Tickets" short="Total" tone="var(--color-primary)"
                onOpen={() => setDrill({ title: 'All my tickets', tickets: userTickets })} />
        <MyStat value={stats.awaiting} label="Awaiting Approval" short="Awaiting" tone="var(--color-info)"
                onOpen={() => setDrill({ title: 'Awaiting approval', tickets: byBucket('awaiting') })} />
        <MyStat value={stats.active} label="In Progress" short="In progress" tone="var(--color-warning)"
                onOpen={() => setDrill({ title: 'In progress', tickets: byBucket('active') })} />
        <MyStat value={stats.completed} label="Completed" short="Done" tone="var(--color-success)"
                onOpen={() => setDrill({ title: 'Completed', tickets: byBucket('completed') })} />
      </div>


      {/* Search */}
      <form className="ud-search" role="search" onSubmit={(e) => e.preventDefault()}>
        <input type="search" id="ud-q" className="ud-search-input" value={q} maxLength={120}
               placeholder="Search my tickets" aria-label="Search my tickets"
               onChange={(e) => setQ(e.target.value)} />
        {urlQ && (
          <span className="ud-search-note" role="status" data-testid="ud-search-note">
            {searching && !found ? 'Searching…'
              : `${(found || []).length} ${(found || []).length === 1 ? 'ticket matches' : 'tickets match'} “${urlQ}”`}
          </span>
        )}
      </form>

      {/* Filters */}
      <div className="ud-filters" role="group" aria-label="Filter tickets">
        <span className="ud-filter-label">Filter:</span>
        {['all', 'awaiting-approval', 'active', 'completed'].map(filterOption => {
          const labels = {
            'all': 'All',
            'awaiting-approval': 'Awaiting Approval',
            'active': 'In Progress',
            'completed': 'Completed'
          };
          // One word each on a phone, so all four fit on one line - the same
          // words as the tiles above them.
          const shortLabels = {
            'all': 'All',
            'awaiting-approval': 'Awaiting',
            'active': 'In progress',
            'completed': 'Done'
          };
          
          return (
            <button
              key={filterOption}
              type="button"
              onClick={() => setFilter(filterOption)}
              className="ud-filter"
              data-on={String(filter === filterOption)}
              aria-pressed={filter === filterOption}
            >
              <span className="ud-long">{labels[filterOption]}</span>
              <span className="ud-short">{shortLabels[filterOption]}</span>
            </button>
          );
        })}
      </div>

      {/* Tickets Grid */}
      {filteredTickets.length === 0 ? (
        <div style={{
          backgroundColor: 'var(--color-bg-alt)',
          border: '2px dashed var(--color-border)',
          borderRadius: '12px',
          padding: '3rem',
          textAlign: 'center',
          color: 'var(--color-text-tertiary)'
        }}>
          <p style={{ fontSize: '1.1rem', margin: '0' }}>
            {urlQ ? (searching && !found ? 'Searching…' : `None of your tickets match “${urlQ}”${filter !== 'all' ? ' in this filter' : ''}`) : 'No tickets found in this filter'}
          </p>
        </div>
      ) : (
        <div className="ud-grid">
          {/* KEYBOARD-REACHABLE. Each card was a <div onClick> with no role,
              no tabIndex and no key handler, and its hover was written into
              the element's style from JavaScript - so from the keyboard the
              staff home list could not be opened at all. The title is a real
              link now; the card itself is also in the tab order and answers
              Enter and Space, with the click kept as the convenience it was.
              The hover is a CSS rule (UserDashboard.extra.css). */}
          {filteredTickets.map(ticket => (
            <div
              key={ticket.id}
              role="link"
              tabIndex={0}
              aria-label={`Open ${ticket.ticket_number}: ${ticket.title || ''}`}
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;   // the link inside handles its own
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/tickets/${ticket.id}`); }
              }}
              className="ud-card"
            >
              {/* Ticket ID and Number */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}>
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontWeight: '700',
                  color: 'var(--color-primary)',
                  backgroundColor: 'var(--color-primary-pale)',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  fontSize: '0.85rem'
                }}>
                  {ticket.ticket_number}
                </span>
                {ticket.approval_status === 'pending' && statusOf(ticket) === 'Awaiting Approval' && (
                  <span style={{
                    backgroundColor: 'var(--color-info-light)',
                    color: 'var(--color-info)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Pending Review
                  </span>
                )}
              </div>

              {/* Title */}
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
                margin: '0 0 0.75rem 0',
                lineHeight: '1.4'
              }}>
                <Link to={`/tickets/${ticket.id}`} className="ud-card-link" tabIndex={-1}
                      onClick={(e) => e.stopPropagation()}>
                  {ticket.title}
                </Link>
              </h3>

              {/* Approval Info */}
              {ticket.approval_status === 'pending' && statusOf(ticket) === 'Awaiting Approval' && (
                <div style={{
                  backgroundColor: 'var(--color-info-light)',
                  border: '1px solid #e9d5ff',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  marginBottom: '0.75rem',
                  fontSize: '0.85rem',
                  color: 'var(--color-info)'
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                    ⏳ Awaiting Approval
                  </div>
                  {ticket.approver_name && (
                    <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                      Reviewer: {ticket.approver_name}
                    </div>
                  )}
                </div>
              )}

              {/* Status and Priority */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '0.75rem',
                flexWrap: 'wrap'
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.4rem 0.75rem',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  ...getStatusBadgeStyle(ticket)
                }}>
                  {statusOf(ticket) || 'Unknown'}
                </span>
                {ticket.priority_name && (
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '0.4rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    ...getPriorityBadgeStyle(ticket.priority_name)
                  }}>
                    {ticket.priority_name}
                  </span>
                )}
              </div>

              {/* Meta Info */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem',
                paddingTop: '0.75rem',
                borderTop: '1px solid var(--color-border)',
                fontSize: '0.8rem',
                color: 'var(--color-text-tertiary)'
              }}>
                <div>
                  <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>
                    Created
                  </span>
                  {ticket.created_at && fmtDate(ticket.created_at)}
                </div>
                {ticket.resolved_at && (
                  <div>
                    <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.25rem' }}>
                      Resolved
                    </span>
                    {fmtDate(ticket.resolved_at)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {drill && (
        <TicketListModal
          title={drill.title}
          tickets={drill.tickets}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

export default UserDashboard;
